<template>
  <div class="flex-fill" @keydown.ctrl.83.prevent.stop="save">
    <v-slide-y-transition mode="out-in">
      <v-container v-if="!notReady" class="py-md-8 mb-16">
        <v-row class="pb-16">
          <v-col cols="12">
            <v-alert
              :value="dbVersion.status === surveyStatus.published.key"
              type="success"
              color="green darken-2"
              elevation="2"
              border="top"
              class="mx-3"
            >
              This survey is <strong>published</strong>. Your options have been
              limited.

              <p class="mb-0 text-caption">
                {{ surveyStatus.published.detail }}
              </p>
            </v-alert>
            <v-alert
              :value="dbVersion.status === surveyStatus.archived.key"
              type="info"
              color="secondary darken-1"
              border="top"
              class="mx-3"
              elevation="2"
            >
              This survey is <strong>archived</strong>. Your options have been
              limited.

              <p class="mb-0 text-caption">
                {{ surveyStatus.archived.detail }}
              </p>
            </v-alert>
          </v-col>
          <v-col cols="12">
            <survey-title-editor v-model="survey.title"></survey-title-editor>
          </v-col>
          <v-col cols="12">
            <v-card class="mx-3">
              <survey-steps-editor
                :pageIndex="pageIndex"
                v-model="survey.categories"
                @change-page="(target) => (pageIndex = target)"
                :locked="locked"
              ></survey-steps-editor>
              <v-divider></v-divider>
              <v-slide-y-transition tag="div" mode="out-in">
                <template v-for="(page, catIndex) in survey.categories">
                  <div :key="catIndex" v-if="catIndex === pageIndex">
                    <survey-page-title-editor
                      v-model="survey.categories[catIndex]"
                      :pageIndex="pageIndex"
                      :pageCount="survey.categories.length"
                      :locked="locked"
                    ></survey-page-title-editor>
                    <v-card-text class="px-md-12 py-md-9 px-6 py-4">
                      <v-row>
                        <template v-for="(question, index) in page.questions">
                          <survey-question-checklist-editor
                            v-if="question.type === questionTypes.checklist"
                            ref="questionEditor"
                            v-model="page.questions[index]"
                            :questionIndex="index"
                            :pages="survey.categories"
                            :pageKey="page.key"
                            :key="question.key"
                            :questionKeys="questionKeys"
                            :questionMappingKeys="questionMappingKeys"
                            :question-number="index"
                            :queryRules="queryRules"
                            :locked="locked"
                            class="mb-16 questionEditor"
                            @set-page="
                              (pageKey) => onSetPage(index, question, pageKey)
                            "
                            @move="
                              (increment) => onMoveQuestion(index, increment)
                            "
                            @delete="confirmDeleteQuestion(index)"
                            @force-delete="onForceDelete(index)"
                          ></survey-question-checklist-editor>
                          <survey-question-table-editor
                            v-if="question.type === questionTypes.table"
                            ref="questionEditor"
                            v-model="page.questions[index]"
                            :questionIndex="index"
                            :pages="survey.categories"
                            :pageKey="page.key"
                            :key="question.key"
                            :questionKeys="questionKeys"
                            :questionMappingKeys="questionMappingKeys"
                            :question-number="index"
                            :queryRules="queryRules"
                            :locked="locked"
                            class="mb-16 questionEditor"
                            @set-page="
                              (pageKey) => onSetPage(index, question, pageKey)
                            "
                            @move="
                              (increment) => onMoveQuestion(index, increment)
                            "
                            @delete="confirmDeleteQuestion(index)"
                            @force-delete="onForceDelete(index)"
                          ></survey-question-table-editor>
                          <survey-question-text-field-editor
                            v-if="question.type === questionTypes.text"
                            ref="questionEditor"
                            v-model="page.questions[index]"
                            :questionIndex="index"
                            :pages="survey.categories"
                            :pageKey="page.key"
                            :key="question.key"
                            :questionKeys="questionKeys"
                            :questionMappingKeys="questionMappingKeys"
                            :question-number="index"
                            :queryRules="queryRules"
                            :locked="locked"
                            class="mb-16 questionEditor"
                            @set-page="
                              (pageKey) => onSetPage(index, question, pageKey)
                            "
                            @move="
                              (increment) => onMoveQuestion(index, increment)
                            "
                            @delete="confirmDeleteQuestion(index)"
                            @force-delete="onForceDelete(index)"
                          ></survey-question-text-field-editor>
                          <survey-question-text-block-editor
                            v-if="question.type === questionTypes.textBlock"
                            ref="questionEditor"
                            v-model="page.questions[index]"
                            :questionIndex="index"
                            :pages="survey.categories"
                            :pageKey="page.key"
                            :key="question.key"
                            :questionKeys="questionKeys"
                            :questionMappingKeys="questionMappingKeys"
                            :question-number="index"
                            :queryRules="queryRules"
                            :locked="locked"
                            class="mb-16 questionEditor"
                            @set-page="
                              (pageKey) => onSetPage(index, question, pageKey)
                            "
                            @move="
                              (increment) => onMoveQuestion(index, increment)
                            "
                            @delete="confirmDeleteQuestion(index)"
                            @force-delete="onForceDelete(index)"
                          ></survey-question-text-block-editor>
                          <survey-question-radio-list-editor
                            v-else-if="question.type === questionTypes.radio"
                            ref="questionEditor"
                            v-model="page.questions[index]"
                            :questionIndex="index"
                            :pages="survey.categories"
                            :pageKey="page.key"
                            :key="question.key"
                            :questionKeys="questionKeys"
                            :questionMappingKeys="questionMappingKeys"
                            :question-number="index"
                            :queryRules="queryRules"
                            :locked="locked"
                            class="mb-16 questionEditor"
                            @set-page="
                              (pageKey) => onSetPage(index, question, pageKey)
                            "
                            @move="
                              (increment) => onMoveQuestion(index, increment)
                            "
                            @delete="confirmDeleteQuestion(index)"
                            @force-delete="onForceDelete(index)"
                          ></survey-question-radio-list-editor>
                          <survey-question-ranked-choice-editor
                            v-else-if="
                              question.type === questionTypes.rankedChoice
                            "
                            ref="questionEditor"
                            v-model="page.questions[index]"
                            :questionIndex="index"
                            :pages="survey.categories"
                            :pageKey="page.key"
                            :key="question.key"
                            :questionKeys="questionKeys"
                            :questionMappingKeys="questionMappingKeys"
                            :question-number="index"
                            :queryRules="queryRules"
                            :locked="locked"
                            class="mb-16 questionEditor"
                            @set-page="
                              (pageKey) => onSetPage(index, question, pageKey)
                            "
                            @move="
                              (increment) => onMoveQuestion(index, increment)
                            "
                            @delete="confirmDeleteQuestion(index)"
                            @force-delete="onForceDelete(index)"
                          ></survey-question-ranked-choice-editor>
                        </template>
                        <v-col cols="12" v-if="!locked">
                          <div class="add-wrapper py-8 text-center rounded">
                            <v-menu offset-y>
                              <template v-slot:activator="{ attrs, on }">
                                <v-btn
                                  v-bind="attrs"
                                  v-on="on"
                                  color="green lighten-2"
                                  elevation="0"
                                  large
                                  >+ Add Question</v-btn
                                >
                              </template>

                              <v-list>
                                <v-list-item
                                  link
                                  v-for="(option, index) in questionTypeOptions"
                                  :key="index"
                                  @click="startNewQuestion(option.value)"
                                >
                                  <v-list-item-title>
                                    {{ option.text }}
                                  </v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </div>
                </template>
              </v-slide-y-transition>
              <survey-page-actions
                class="px-md-12 px-6 pb-md-8 pb-6"
                v-model="pageIndex"
                :pages="survey.categories"
                :formData="formData"
                :byPassValidation="true"
                :skipEmpty="false"
              ></survey-page-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-slide-y-transition>
    <v-expand-transition>
      <div class="survey-action-bar grey darken-4 elevation-3" v-if="!notReady">
        <v-container class="d-flex">
          <v-btn @click.prevent="back" dark class="mr-2">
            <v-icon small class="mr-1">$back</v-icon> Go Back
          </v-btn>
          <v-btn
            @click.prevent="showSettings = true"
            class="mr-2"
            elevation="0"
          >
            <v-icon small class="mr-1">$settings</v-icon> Settings
          </v-btn>
          <!-- <v-btn
            @click.prevent="showAttachments = true"
            class="mr-2"
            elevation="0"
          >
            <v-icon small class="mr-1">$attachment</v-icon> Attachments
          </v-btn> -->
          <v-btn
            :href="testURL"
            target="_blank"
            :color="`yellow darken-1 ${unchanged ? 'black--text' : ''}`"
            class="mr-2"
            dark
            :disabled="!unchanged"
            v-if="dbVersion.status == surveyStatus.draft.key"
          >
            <v-icon small class="mr-1">$test</v-icon> Test Survey
          </v-btn>
          <v-btn
            :href="surveyURL"
            target="_blank"
            color="yellow darken-1"
            class="mr-2"
            v-else-if="dbVersion.status == surveyStatus.published.key"
          >
            Goto Survey <v-icon class="ml-2">$go</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-slide-y-reverse-transition>
            <div v-if="!unchanged">
              <v-btn text dark @click="cancel" :disabled="loading">
                Cancel
              </v-btn>
              <v-btn
                color="primary lighten-1"
                dark
                @click="save"
                :disabled="loading"
              >
                Save
              </v-btn>
            </div>
          </v-slide-y-reverse-transition>
        </v-container>
      </div>
    </v-expand-transition>

    <v-dialog v-model="loading" persistent width="300">
      <v-card>
        <v-card-text class="text-center pt-4">
          {{ statusMsg }}
          <v-progress-linear
            indeterminate
            color="primary"
            height="6"
            class="mb-0 mt-2"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showSettings"
      max-width="600px"
      :persistent="isNew"
      scrollable
    >
      <ValidationObserver
        ref="settingsObserver"
        tag="form"
        @submit.prevent="onSettingsSubmit"
      >
        <v-card>
          <dialog-title
            v-model="showSettings"
            :title="`Survey Settings`"
            :disabled="loading"
            :useClose="!isNew"
          ></dialog-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                Use the form below to configure your survey's settings.
              </v-col>
              <v-col cols="4">
                <ValidationProvider
                  rules="required|max:255"
                  v-slot="{ errors }"
                  vid="SurveyId"
                >
                  <v-text-field
                    v-model="settingsSurveyId"
                    label="Survey Id*"
                    outlined
                    :error-messages="errors"
                    :disabled="loading || !isNew"
                    persistent-hint
                    hint="Cannot be changed once set"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="8">
                <ValidationProvider
                  rules="required|max:255"
                  v-slot="{ errors }"
                  vid="Name"
                >
                  <v-text-field
                    v-model="settings.name"
                    label="Name*"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  vid="Status"
                >
                  <v-select
                    v-model="settings.status"
                    label="Status*"
                    outlined
                    :error-messages="errors"
                    :items="surveyStatusOptions"
                    :disabled="loading || isNew"
                    :hint="surveyStatus[settings.status].detail"
                    persistent-hint
                  ></v-select>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" sm="6">
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  vid="startDate"
                >
                  <v-datetime-picker
                    v-model="settings.startDate"
                    label="Start Date*"
                    :text-field-props="{
                      outlined: true,
                      disabled: loading,
                      'error-messages': errors,
                    }"
                    date-format="MM/dd/yyyy"
                    time-format="@ hh:mm aa"
                  >
                    <template slot="dateIcon">
                      <v-icon>$date</v-icon>
                    </template>
                    <template slot="timeIcon">
                      <v-icon>$time</v-icon>
                    </template>
                    <template slot="actions" slot-scope="{ parent }">
                      <v-btn color="primary" @click="parent.okHandler">
                        OK
                      </v-btn>
                    </template>
                  </v-datetime-picker>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" sm="6">
                <ValidationProvider v-slot="{ errors }" vid="endDate">
                  <v-datetime-picker
                    v-model="settings.endDate"
                    label="End Date"
                    :text-field-props="{
                      outlined: true,
                      disabled: loading,
                      'error-messages': errors,
                    }"
                    date-format="MM/dd/yyyy"
                    time-format="@ hh:mm aa"
                  >
                    <template slot="dateIcon">
                      <v-icon>$date</v-icon>
                    </template>
                    <template slot="timeIcon">
                      <v-icon>$time</v-icon>
                    </template>
                  </v-datetime-picker>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="ResponseCountTarget"
                >
                  <v-text-field
                    v-model="settings.responseCountTarget"
                    type="number"
                    label="Response Goal"
                    hint="Used to calculate response rate goals."
                    persistent-hint
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" class="pb-0">
                <v-divider></v-divider>
                <div class="text-h6 mt-3 mb-0">Portal Settings</div>
              </v-col>
              <v-col cols="12">
                <v-checkbox label="Use Portal" v-model="settings.availableInPortal"></v-checkbox>
              </v-col>
              <v-col cols="12" class="d-flex align-center">
                <small class="flex-fill">*indicates required field</small>
                <v-btn
                  :disabled="loading"
                  text
                  color="primary"
                  @click="isNew ? back() : (showSettings = false)"
                >
                  Cancel
                </v-btn>
                <v-btn :loading="loading" type="submit" color="primary">
                  Save
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <v-dialog
      v-model="showAttachments"
      max-width="600px"
      :persistent="isNew"
      scrollable
      v-if="survey.attachments"
    >
      <v-card>
        <dialog-title
          v-model="showAttachments"
          title="Attachments"
          :disabled="loading"
          :useClose="true"
        ></dialog-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-list>
            <template v-for="(item, i) in survey.attachments">
              <v-list-item :key="item.blobName" class="px-0">
                <v-list-item-content class="flex-column align-stretch">
                  <div class="d-flex align-center">
                    <div class="flex-fill">
                      <strong>{{ item.label }}</strong>
                    </div>

                    <v-btn icon @click.stop="editAttachment(item)"
                      ><v-icon>$edit</v-icon></v-btn
                    >
                    <v-btn icon @click.stop="downloadAttachment(item)"
                      ><v-icon>$download</v-icon></v-btn
                    >
                    <v-btn icon @click.stop="deleteAttachment(item)"
                      ><v-icon>$delete</v-icon></v-btn
                    >
                  </div>
                  <div class="text-caption">{{ item.description }}</div>
                </v-list-item-content>
              </v-list-item>

              <v-divider :key="'divider_' + i" class="mb-1"></v-divider>
            </template>

            <v-list-item
              v-if="survey.attachments.length === 0"
              class="px-0 grey lighten-2 rounded mt-2"
            >
              <v-list-item-content class="py-8 d-block text-center">
                <i
                  class="rounded-pill grey lighten-2 grey--text text--darken-1 mx-auto text-caption d-inline px-2 py-1"
                >
                  None Found
                </i>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-btn block color="primary" @click="editAttachment(null)"
            >Add Attachment</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showAttachment"
      max-width="600px"
      :persistent="isNewAttachment"
      scrollable
    >
      <ValidationObserver
        ref="attachmentObserver"
        tag="form"
        @submit.prevent="onEditAttachment"
      >
        <v-card>
          <dialog-title
            v-model="showAttachment"
            :title="`${isNewAttachment ? 'Add' : 'Edit'} Attachment`"
            :disabled="loading"
          ></dialog-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                Use the form below to manage your attachment.
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  rules="required|max:255"
                  v-slot="{ errors }"
                  vid="Name"
                >
                  <v-text-field
                    v-model="attachment.name"
                    label="Name*"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  rules="max:2000"
                  v-slot="{ errors }"
                  vid="Description"
                >
                  <v-textarea
                    v-model="attachment.description"
                    label="Description"
                    outlined
                    :error-messages="errors"
                    :disabled="loading"
                  ></v-textarea>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  v-slot="{ errors, classes }"
                  name="selectedFile"
                  vid="selectedFile"
                  :rules="isNewAttachment ? 'required' : ''"
                >
                  <v-file-input
                    v-model="selectedFile"
                    label="Attachment"
                    :multiple="false"
                    outlined
                    prepend-icon=""
                    :error="errors.length > 0"
                    :error-messages="errors[0]"
                    :class="classes"
                    prepend-inner-icon="$file"
                  ></v-file-input>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" class="d-flex align-center">
                <small class="flex-fill">*indicates required field</small>
                <v-btn
                  :disabled="loading"
                  @click="showAttachment = false"
                  text
                  color="primary"
                >
                  Cancel
                </v-btn>
                <v-btn :loading="loading" type="submit" color="primary">
                  Save
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </ValidationObserver>
    </v-dialog>

    <v-dialog v-model="openConfirmDelete" max-width="400px">
      <v-card>
        <dialog-title
          v-model="openConfirmDelete"
          title="Confirm Delete"
          :disabled="loading"
        ></dialog-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to delete question:
                <strong class="red--text darken-1 font-weight-bold">{{
                  deleting > -1
                    ? survey.categories[pageIndex].questions[deleting].key
                    : "--"
                }}</strong
                >. <br /><br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="cancelDeleteQuestion"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="deleteQuestion"
              >
                Yes, delete
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import equal from "deep-is";

import surveyService from "@/services/surveyService";
import SurveyTitleEditor from "@/components/Survey.Title.Editor.vue";
import SurveyPageActions from "../../components/Survey.PageActions.vue";
import SurveyPageTitleEditor from "@/components/Survey.PageTitle.Editor.vue";
import SurveyQuestionChecklistEditor from "@/components/Survey.Question.Checklist.Editor.vue";
import SurveyQuestionTableEditor from "@/components/Survey.Question.Table.Editor.vue";
import SurveyQuestionTextFieldEditor from "@/components/Survey.Question.TextField.Editor.vue";
import SurveyQuestionTextBlockEditor from "@/components/Survey.Question.TextBlock.Editor.vue";
import SurveyStepsEditor from "@/components/Survey.Steps.Editor.vue";
import SurveyQuestionRadioListEditor from "@/components/Survey.Question.RadioList.Editor.vue";
import SurveyQuestionRankedChoiceEditor from "@/components/Survey.Question.RankedChoice.Editor.vue";

import {
  questionTypeOptions,
  questionTypes,
  surveyStatus,
  surveyTemplateTypes,
} from "../../constants/Survey";
import DialogTitle from "../../components/DialogTitle.vue";
import {
  dtFromServerString,
  formattedTimestamp,
} from "../../utilities/dataUtilities";

const newSurvey = (tenantId) => ({
  id: null,
  tenantId,
  accessKeyChars: "ABCDE234789",
  accessKeyLength: 5,
  categories: [],
  endDate: null,
  name: "",
  startDate: null,
  status: "draft",
  title: null,
  responseCountTarget: 0,
  availableInPortal: false,
});

const newAttachment = () => ({
  label: "",
  description: "",
  fileName: "",
  blobName: "",
});

export default {
  components: {
    DialogTitle,
    SurveyStepsEditor,
    SurveyTitleEditor,
    SurveyPageTitleEditor,
    SurveyQuestionChecklistEditor,
    SurveyQuestionTableEditor,
    SurveyQuestionTextBlockEditor,
    SurveyQuestionTextFieldEditor,
    SurveyQuestionRadioListEditor,
    SurveyQuestionRankedChoiceEditor,
    SurveyPageActions,
  },
  data: () => ({
    notReady: true,
    pendingStateChanges: 0,
    statusMsg: "",
    dbVersion: null,
    survey: {
      categories: [],
    },
    pageIndex: 0,
    openConfirmDelete: false,
    deleting: -1,
    showSettings: false,
    showAttachments: false,
    showAttachment: false,
    formData: [],
    settings: {
      _surveyId: null,
      name: null,
      startDate: null,
      endDate: null,
      responseCountTarget: null,
      availableInPortal: false,
      enablePortalCodeManagement: false,
    },
    isNewAttachment: true,
    attachment: newAttachment(),
  }),
  computed: {
    unchanged() {
      return equal(this.dbVersion, this.survey);
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    tenantId() {
      return this.$store.state.tenantId;
    },
    surveyId() {
      return this.$route.params.id;
    },
    questionKeys() {
      let keys = [];

      for (let i = 0; i < this.survey.categories.length; i++)
        getKeys(this.survey.categories[i].questions);

      function getKeys(questions) {
        for (let i = 0; i < questions.length; i++) {
          if (questions[i].children) getKeys(questions[i].children);
          keys.push(questions[i].key);
        }
      }

      return keys;
    },
    questionMappingKeys() {
      let keys = [];

      for (let i = 0; i < this.survey.categories.length; i++)
        getKeys(this.survey.categories[i].questions);

      function getKeys(questions) {
        for (let i = 0; i < questions.length; i++) {
          if (questions[i].children) getKeys(questions[i].children);
          keys.push(questions[i].mappingKey);
        }
      }

      return keys;
    },
    questionTypeOptions() {
      return questionTypeOptions;
    },
    questionTypes() {
      return questionTypes;
    },
    queryRules() {
      let rules = [];

      for (let i = 0; i < this.survey.categories.length; i++)
        getRules(this.survey.categories[i].questions);

      function getRules(questions) {
        for (let i = 0; i < questions.length; i++) {
          switch (questions[i].type) {
            case questionTypes.checklist: {
              for (let ii = 0; ii < questions[i].children.length; ii++) {
                let rule = {
                  id: questions[i].children[ii].key,
                  label: questions[i].children[ii].key,
                  type: "select",
                  operators: ["="],
                  choices: ["true", "false"].map((x) => ({
                    label: x,
                    value: x,
                  })),
                };
                rules.push(rule);
              }

              break;
            }
            case questionTypes.table: {
              for (let ii = 0; ii < questions[i].children.length; ii++) {
                let rule = {
                  id: questions[i].children[ii].key,
                  label: questions[i].children[ii].key,
                  type: "select",
                  operators: ["="],
                  choices: questions[i].options.map((x) => ({
                    label: x.text,
                    value: x.value,
                  })),
                };
                rules.push(rule);
              }

              break;
            }
            case questionTypes.radio: {
              let rule = {
                id: questions[i].key,
                label: questions[i].key,
                type: "select",
                operators: ["="],
                choices: questions[i].options
                  ? questions[i].options.map((x) => ({
                      label: x.text,
                      value: x.value,
                    }))
                  : [],
              };
              rules.push(rule);
              break;
            }
            default: {
              // console.warn(
              //   `Could not get rule. '${questions[i].type}' is an unsupported type'`
              // );
            }
          }
        }
      }

      return rules.sort((a, b) => (a.label > b.label ? 1 : -1));
    },
    surveyStatusOptions() {
      let options = [];

      for (let key in surveyStatus) {
        options.push({
          text: surveyStatus[key].name,
          value: surveyStatus[key].key,
        });
      }

      return options;
    },
    surveyStatus() {
      return surveyStatus;
    },
    locked() {
      return [surveyStatus.published.key, surveyStatus.archived.key].some(
        (x) => this.dbVersion.status == x
      );
    },
    settingsSurveyId: {
      get() {
        return this.settings._surveyId;
      },
      set(val) {
        this.settings._surveyId =
          val != "new" ? val?.replace(/[^A-Z0-9]+/gi, "_") : "";
      },
    },
    isNew() {
      return this.surveyId == "new";
    },
    tenant() {
      return this.$store.getters["tenants/tenant"];
    },
    surveyURL() {
      if (this.tenant.surveyTemplateType === surveyTemplateTypes.custom) {
        return `#/survey/${this.tenantId}/${this.surveyId}`;
      } else return `#/survey/s/${this.tenantId}/${this.surveyId}`;
    },
    testURL() {
      return this.surveyURL + "?testing=true";
    },
  },
  watch: {
    pageIndex() {
      this.$vuetify.goTo(document.querySelector("#surveySteps"), {
        duration: 0,
      });
    },
    showSettings(val) {
      if (val) {
        this.settingsSurveyId = this.surveyId ?? "";
        this.settings.status = this.survey.status + ""; // ensure mutation safety
        this.settings.name = this.survey.name + ""; // ensure mutation safety
        this.settings.startDate = this.survey.startDate
          ? dtFromServerString(this.survey.startDate)
          : "";
        this.settings.endDate = this.survey.startDate
          ? dtFromServerString(this.survey.endDate)
          : "";

        this.settings.responseCountTarget = isNaN(
          this.survey.responseCountTarget
        )
          ? null
          : this.survey.responseCountTarget + 0;
        this.settings.availableInPortal =
          this.survey.availableInPortal ?? false;
        this.settings.enablePortalCodeManagement =
          this.survey.enablePortalCodeManagement ?? false;
      }
    },
  },
  methods: {
    load() {
      this.pendingStateChanges++;
      this.statusMsg = "Loading Survey";

      surveyService
        .get(this.tenantId, this.surveyId)
        .then(
          (result) => {
            if (!Array.isArray(result.attachments)) result.attachments = [];
            this.dbVersion = JSON.parse(JSON.stringify(result));
            this.survey = JSON.parse(JSON.stringify(result));
            if (this.survey.categories.length == 0) {
              this.survey.categories.push({
                displayName: "Page Title",
                key: "P1",
                questions: [],
                rules: [],
              });
            }
            this.notReady = false;
          },
          (error) => {
            console.warn(error);
            this.back();
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    save() {
      this.pendingStateChanges++;
      this.statusMsg = "Saving Changes";

      return surveyService
        .update(this.tenantId, this.surveyId, this.survey)
        .then(
          () => {
            this.$store.dispatch(
              "notifications/success",
              "Successfully saved changes."
            );

            this.dbVersion = JSON.parse(JSON.stringify(this.survey));
          },
          (error) => {
            console.warn(error);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while loading survey. Please try again later"
            );
            this.back();
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    cancel() {
      this.survey = JSON.parse(JSON.stringify(this.dbVersion));
    },
    back() {
      this.$router.go(-1);
    },
    onSetPage(index, question, pageKey) {
      let targetIndex = this.survey.categories.findIndex(
        (x) => x.key === pageKey
      );

      this.survey.categories[targetIndex].questions.push(question);
      this.survey.categories[this.pageIndex].questions.splice(index, 1);
      this.pageIndex = targetIndex;
      setTimeout(() => {
        let editors = document.querySelectorAll(".questionEditor");
        let last = editors[editors.length - 1];
        this.$vuetify.goTo(last, {
          duration: 200 * editors.length,
          offset: 20,
        });
      }, 800);
    },
    onMoveQuestion(index, increment) {
      this.$nextTick(() => {
        let mirror = JSON.parse(
          JSON.stringify(this.survey.categories[this.pageIndex])
        );
        let old = mirror.questions[index + increment];

        mirror.questions[index + increment] = mirror.questions[index];
        mirror.questions[index] = old;

        Vue.set(this.survey.categories, this.pageIndex, mirror);
      });
    },
    confirmDeleteQuestion(index) {
      this.deleting = index;
      this.openConfirmDelete = true;
    },
    deleteQuestion() {
      this.survey.categories[this.pageIndex].questions.splice(this.deleting, 1);
      this.cancelDeleteQuestion();
    },
    onForceDelete(index) {
      this.deleting = index;
      this.deleteQuestion();
    },
    cancelDeleteQuestion() {
      this.deleting = -1;
      this.openConfirmDelete = false;
    },
    startNewQuestion(type) {
      let question;

      switch (type) {
        case questionTypes.checklist:
        case questionTypes.table: {
          question = {
            type,
            key: "",
            mappingKey: "",
            questionNumber: "",
            text: "",
            children: [],
            options: [],
            settings: [],
            rules: [],
          };
          break;
        }
        case questionTypes.text:
        case questionTypes.textBlock:
        case questionTypes.radio:
        case questionTypes.rankedChoice: {
          question = {
            type,
            key: "",
            mappingKey: "",
            questionNumber: "",
            text: "",
            children: null,
            options: [],
            settings: [],
            rules: [],
          };
          break;
        }
        default:
          return;
      }

      this.survey.categories[this.pageIndex].questions.push(question);

      setTimeout(() =>
        this.$refs.questionEditor[
          this.survey.categories[this.pageIndex].questions.length - 1
        ].edit()
      );
    },
    async onSettingsSubmit() {
      let isValid = await this.$refs.settingsObserver.validate();
      if (!isValid) return;

      this.pendingStateChanges++;

      const updateSettings = (data) => {
        data.id = this.settingsSurveyId;
        data.tenantId = this.tenantId;
        data.name = this.settings.name;
        if (!data.title) data.title = data.name;
        data.startDate = formattedTimestamp(this.settings.startDate);
        data.endDate = formattedTimestamp(this.settings.endDate);
        data.status = this.settings.status;
        data.responseCountTarget =
          !this.settings.responseCountTarget ||
          isNaN(this.settings.responseCountTarget) ||
          this.settings.responseCountTarget.length === 0
            ? null
            : this.settings.responseCountTarget;

        data.availableInPortal = this.settings.availableInPortal ?? false;
        data.enablePortalCodeManagement =
          this.settings.enablePortalCodeManagement ?? false;
      };

      let survey = JSON.parse(JSON.stringify(this.dbVersion));
      updateSettings(survey);

      this.showSettings = false;

      this.statusMsg = "Saving Settings";

      let task = this.isNew
        ? surveyService.create(this.tenantId, survey)
        : surveyService.update(this.tenantId, this.surveyId, survey);

      return task
        .then(
          () => {
            this.$store.dispatch(
              "notifications/success",
              "Successfully saved settings."
            );

            this.dbVersion = JSON.parse(JSON.stringify(survey));
            updateSettings(this.survey);

            if (this.isNew) {
              this.$router.replace({
                name: "editor",
                params: { id: this.survey.id },
              });
              this.load();
            }
          },
          (error) => {
            console.warn(error);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while saving settings. Please try again later"
            );
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    editAttachment(item) {
      if (item) {
        this.isNewAttachment = false;
        this.attachment = JSON.parse(JSON.stringify(item));
      } else {
        this.isNewAttachment = true;
        this.attachment = newAttachment();
      }

      this.$refs.attachmentObserver?.reset();
      this.showAttachment = true;
    },
    async onEditAttachment() {},
    deleteAttachment(item) {
      console.log("NOT IMPLEMENTED");
      return item;
    },
    downloadAttachment(item) {
      console.log("NOT IMPLEMENTED");
      return item;
    },
  },
  mounted() {
    if (this.isNew) {
      if (this.$route.query.clone) {
        this.pendingStateChanges++;
        this.statusMsg = "Loading Survey Data";

        surveyService
          .get(this.tenantId, this.$route.query.clone)
          .then(
            (result) => {
              delete result.surveyId;
              this.dbVersion = JSON.parse(JSON.stringify(result));
              this.survey = JSON.parse(JSON.stringify(result));
              this.showSettings = true;
            },
            (error) => {
              console.warn(error);
              this.$store.dispatch(
                "notifications/error",
                "An unexpected error occurred while loading survey. Please try again later"
              );
              this.back();
            }
          )
          .finally(() => this.pendingStateChanges--);
      } else {
        this.survey = newSurvey(this.tenantId);
        this.dbVersion = newSurvey(this.tenantId);
        this.showSettings = true;
      }
    } else {
      this.load();
    }
    this.$store.commit("notifications/setOffset", { x: 0, y: 58 });
  },
  unmounted() {
    this.load();
    this.$store.commit("notifications/setOffset", { x: 0, y: 0 });
  },
};
</script>

<style>
.survey-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
}

.add-wrapper {
  border: 2px dashed #ddd !important;
  background: #f5f5f5;
}
</style>
